import { Link } from "react-router-dom";
import StatusBadge from "@/components/base/StatusBadge";
import CredibilityMeter from "@/components/base/CredibilityMeter";

type Topic = {
  id: string;
  title: string;
  category: string;
  status: string;
  credibility: number;
  heat: number;
  date: string;
  evidenceCount: number;
  summary: string;
  tags: string[];
  cover: string;
};

type Props = {
  topic: Topic;
  variant?: "default" | "featured" | "row";
};

export default function TopicCard({ topic, variant = "default" }: Props) {
  if (variant === "row") {
    return (
      <Link
        to={`/topic/${topic.id}`}
        className="group flex items-center gap-4 rounded-lg border border-background-300 bg-background-100 p-3 transition-colors hover:border-primary-300 hover:bg-background-200 cursor-pointer"
      >
        <div className="relative h-16 w-24 shrink-0 overflow-hidden rounded-md">
          <img src={topic.cover} alt={topic.title} className="h-full w-full object-cover" />
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <span className="font-label text-[11px] text-primary-700">{topic.category}</span>
            <span className="font-label text-[11px] text-foreground-500">{topic.date}</span>
          </div>
          <h3 className="mt-1 truncate font-heading text-sm font-semibold text-foreground-900 group-hover:text-primary-700">
            {topic.title}
          </h3>
        </div>
        <StatusBadge status={topic.status} />
      </Link>
    );
  }

  if (variant === "featured") {
    return (
      <Link
        to={`/topic/${topic.id}`}
        className="group flex flex-col overflow-hidden rounded-lg border border-background-300 bg-background-100 transition-colors hover:border-primary-400 cursor-pointer"
      >
        <div className="relative h-[280px] w-full overflow-hidden">
          <img
            src={topic.cover}
            alt={topic.title}
            className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-foreground-950/70 via-foreground-950/10 to-transparent" />
          <div className="absolute left-4 top-4 flex items-center gap-2">
            <StatusBadge status={topic.status} />
            <span className="rounded-md bg-background-50/90 px-2.5 py-1 font-label text-xs text-foreground-800">
              {topic.category}
            </span>
          </div>
          <div className="absolute bottom-0 w-full p-5">
            <h3 className="font-heading text-xl font-semibold leading-snug text-background-50">
              {topic.title}
            </h3>
            <p className="mt-2 line-clamp-2 text-sm text-background-200">{topic.summary}</p>
          </div>
        </div>
        <div className="flex items-center justify-between gap-4 px-5 py-4">
          <div className="w-32">
            <CredibilityMeter score={topic.credibility} showLabel={false} compact />
          </div>
          <div className="flex items-center gap-3 font-label text-xs text-foreground-500">
            <span className="inline-flex items-center gap-1">
              <i className="ri-file-list-3-line" /> {topic.evidenceCount} 项依据
            </span>
            <span className="inline-flex items-center gap-1">
              <i className="ri-fire-line" /> {topic.heat}
            </span>
          </div>
        </div>
      </Link>
    );
  }

  return (
    <article className="group flex h-full flex-col overflow-hidden rounded-lg border border-background-300 bg-background-100 transition-colors hover:border-primary-300">
      <Link to={`/topic/${topic.id}`} className="block cursor-pointer">
        <div className="relative h-40 w-full overflow-hidden">
          <img
            src={topic.cover}
            alt={topic.title}
            className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
          />
          <div className="absolute left-3 top-3">
            <StatusBadge status={topic.status} />
          </div>
        </div>
      </Link>
      <div className="flex flex-1 flex-col p-5">
        <div className="flex items-center gap-2 font-label text-[11px] text-foreground-500">
          <span className="text-primary-700">{topic.category}</span>
          <span className="h-3 w-px bg-background-400" />
          <span>{topic.date}</span>
        </div>
        <h3 className="mt-2 font-heading text-base font-semibold leading-snug text-foreground-900">
          <Link to={`/topic/${topic.id}`} className="cursor-pointer hover:text-primary-700">
            {topic.title}
          </Link>
        </h3>
        <p className="mt-2 line-clamp-3 flex-1 text-sm leading-relaxed text-foreground-600">
          {topic.summary}
        </p>
        <div className="mt-4">
          <CredibilityMeter score={topic.credibility} showLabel={false} compact />
        </div>
        <div className="mt-3 flex items-center justify-between border-t border-background-200 pt-3">
          <span className="font-label text-[11px] text-foreground-500">
            {topic.evidenceCount} 项依据 · 热度 {topic.heat}
          </span>
          <Link
            to={`/topic/${topic.id}`}
            className="inline-flex items-center gap-1 font-label text-xs text-primary-700 hover:text-primary-600 cursor-pointer"
          >
            查看依据 <i className="ri-arrow-right-line" />
          </Link>
        </div>
      </div>
    </article>
  );
}